import { useTranslation } from "react-i18next";
import { DocumentText1, Calendar, Building, Shield } from "iconsax-react";
import { FileViewer } from "@/shared/ui";
import { formatDateForDisplay } from "@/shared/utils";
import type { IdDocumentsSection as IdDocumentsSectionType } from "../../types/personalSheet";
import ProfileDataRow from "../ProfileDataRow";

interface Props {
  idDocuments: IdDocumentsSectionType | null;
}

const MILITARY_OBLIGATION_LABEL_KEYS: Record<string, string> = {
  "призывник": "personalSheet.militaryObligationOptions.conscript",
  "военнообязанный": "personalSheet.militaryObligationOptions.military",
  "не военнообязанный": "personalSheet.militaryObligationOptions.exempt",
};

export default function IdDocumentsSection({ idDocuments }: Props) {
  const { t } = useTranslation("EmployeePortal");

  if (!idDocuments) {
    return <p className="text-body-regular-md content-base-secondary">{t("personalSheet.empty.idDocuments")}</p>;
  }

  const militaryRelation = idDocuments.military_obligation_relation ?? "";
  const isNoPriorTraining = militaryRelation === "не военнообязанный" || militaryRelation === "призывник";
  const militaryRelationLabelKey = MILITARY_OBLIGATION_LABEL_KEYS[militaryRelation.trim().toLowerCase()];
  const militaryRelationValue = militaryRelation ? (militaryRelationLabelKey ? t(militaryRelationLabelKey) : militaryRelation) : null;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col">
        <ProfileDataRow icon={DocumentText1} label={t("personalSheet.fields.nationalIdNumber")} value={idDocuments.national_id_number} />
        <ProfileDataRow icon={Calendar} label={t("personalSheet.fields.nationalIdIssueDate")} value={formatDateForDisplay(idDocuments.national_id_issue_date)} />
        <ProfileDataRow icon={Calendar} label={t("personalSheet.fields.nationalIdExpiryDate")} value={formatDateForDisplay(idDocuments.national_id_expiry_date)} />
        <ProfileDataRow icon={Building} label={t("personalSheet.fields.nationalIdIssuedBy")} value={idDocuments.national_id_issued_by} />
        {militaryRelationValue && (
          <ProfileDataRow icon={Shield} label={t("personalSheet.fields.militaryObligationRelation")} value={militaryRelationValue} />
        )}
        {!isNoPriorTraining && idDocuments.military_rank && (
          <ProfileDataRow icon={Shield} label={t("personalSheet.fields.militaryRank")} value={idDocuments.military_rank} />
        )}
        {!isNoPriorTraining && idDocuments.military_composition && (
          <ProfileDataRow icon={Shield} label={t("personalSheet.fields.militaryComposition")} value={idDocuments.military_composition} />
        )}
        {!isNoPriorTraining && idDocuments.military_branch && (
          <ProfileDataRow icon={Shield} label={t("personalSheet.fields.militaryBranch")} value={idDocuments.military_branch} />
        )}
      </div>
      {idDocuments.national_id_file_url && (
        <div className="flex flex-col gap-1.5">
          <span className="text-label-sm content-base-secondary">{t("personalSheet.fields.nationalIdFile")}</span>
          <FileViewer existingFileUrl={idDocuments.national_id_file_url} isDownloadable />
        </div>
      )}
      {idDocuments.military_certificate_file_url && (
        <div className="flex flex-col gap-1.5">
          <span className="text-label-sm content-base-secondary">{t("personalSheet.fields.militaryCertificate")}</span>
          <FileViewer existingFileUrl={idDocuments.military_certificate_file_url} isDownloadable />
        </div>
      )}
    </div>
  );
}
